import { resolveProductInventory } from './store/inventoryAvailability.js'
import { productApi } from '@/api/store/product'
import { storeApi } from '@/api/store/index'
import {
  getPromotionDetailPriceInfo,
  isPromotionDetailCatalogPriceType,
  isPromotionDetailInProgress,
  mapPromotionDetailToProductPatch,
  pickPromotionDetailProduct,
  resolvePromotionDetailCatalogPrice,
  resolvePromotionDetailRequest,
  resolvePromotionDetailTimeRange,
  unwrapPromotionDetail,
} from '@/utils/store/promotionDetail'
import { selectAppliedTier } from '@/utils/store/promotionTagResolution'
import {
  appendOssImageProcess,
  dateToTimestamp,
  formatDate,
  formatPrice,
  getCountdown,
  getOssLimitedWidthUrl,
  getOssOriginUrl,
} from './index'
import { mapLegacyPromotionType, PROMOTION_TYPE } from '@/utils/store/promotionTypes'
import { comboPackageGroupKeyFromRow } from '@/utils/store/type3PackageGroupKey'
import { EMPTY_VALUE_PLACEHOLDER } from './constant'

const STORE_PRODUCT_INTRO_WEBP_PROCESS = 'format,webp'

/**
 * @description 商品身份标签
 */
export const PRODUCT_IDENTITY_TAGS = ['新品', '热销', '甄选']

/**
 * @description 商品运营标签编码
 */
export const PRODUCT_TAG_CODE = {
  SELECTED: '1',
  NEW: '2',
  HOT: '3',
}

/**
 * @description 商品活动标签
 */
export const PRODUCT_ACTIVITY_TAGS = ['特价', '折扣', '买赠', '赠品', '秒杀']

export const PRODUCT_ACTIVITY_TAG_BY_PROMOTION_TYPE = {
  [PROMOTION_TYPE.VOLUME_PRICE]: '特价',
  [PROMOTION_TYPE.VOLUME_DISCOUNT]: '折扣',
  [PROMOTION_TYPE.BUY_GIFT]: '买赠',
}

const PRODUCT_ACTIVITY_TAG_ALIASES = {
  特价: '特价',
  秒杀: '特价',
  折扣: '折扣',
  满折: '折扣',
  买赠: '买赠',
  赠品: '买赠',
}

/**
 * @description 解析商品标题前展示的活动类型标签，仅展示特价、折扣、买赠
 * @param {Object|null|undefined} product
 * @returns {string}
 */
export const resolveProductActivityTag = (product) => {
  if (!product || typeof product !== 'object') return ''

  const type = Number(product.promotionType ?? product.promotion_type ?? product.activityType)
  if (PRODUCT_ACTIVITY_TAG_BY_PROMOTION_TYPE[type]) {
    return PRODUCT_ACTIVITY_TAG_BY_PROMOTION_TYPE[type]
  }

  const directCandidates = [
    product.activityTag,
    product.activityLabel,
    product.promotionTag,
    product.promotionLabel,
    product.activityTypeText,
  ]

  for (const item of directCandidates) {
    const tag = PRODUCT_ACTIVITY_TAG_ALIASES[String(item || '').trim()]
    if (tag) return tag
  }

  const rawTags = Array.isArray(product.labelList)
    ? product.labelList
    : Array.isArray(product.tags)
      ? product.tags
      : product.tag
        ? [product.tag]
        : []

  for (const item of rawTags) {
    const tag = PRODUCT_ACTIVITY_TAG_ALIASES[String(item || '').trim()]
    if (tag) return tag
  }

  return ''
}

/**
 * @description 解析商品标题前展示的活动标签列表（优先 `/p/store/product/filter/search` 的 actTags）
 * @param {Object|null|undefined} product
 * @returns {string[]}
 */
export const resolveProductActTags = (product) => {
  if (!product || typeof product !== 'object') return []

  const raw = product.actTags
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((item) => String(item ?? '').trim()).filter(Boolean)
  }

  const fallback = resolveProductActivityTag(product)
  return fallback ? [fallback] : []
}

/**
 * @description 获取排序后的商品标签
 * @param {Array} tags - 原始标签数组
 * @returns {Array} 排序后的标签数组
 */
export const getSortedProductTags = (tags) => {
  if (!tags || !tags.length) return []

  return [...tags].sort((a, b) => {
    const getOrder = (tag) => {
      const iIdx = PRODUCT_IDENTITY_TAGS.indexOf(tag)
      if (iIdx !== -1) return iIdx // 0, 1, 2

      const aIdx = PRODUCT_ACTIVITY_TAGS.indexOf(tag)
      if (aIdx !== -1) return PRODUCT_IDENTITY_TAGS.length + aIdx // 3, 4

      return 999
    }
    return getOrder(a) - getOrder(b)
  })
}

/**
 * @description 归一化商品数据 (处理秒杀搜索结果等)
 * @param {Object} product
 * @returns {Object}
 */
export const normalizeProductData = (product) => {
  if (!product) return null
  if (!product.seckillSearchVO) return product
  return {
    ...product,
    seckillDetail: {
      ...product,
      ...(product.seckillSearchVO || {}),
    },
  }
}

/**
 * @description 归一化套餐详情响应（兼容 axios 已解包与仍含 data 嵌套）
 * @param {unknown} raw
 * @returns {Record<string, unknown>|null}
 */
export const normalizeComboDetailPayload = (raw) => {
  if (!raw || typeof raw !== 'object') return null
  const o = /** @type {Record<string, unknown>} */ (raw)
  if (o.comboId != null || o.mainProd != null || o.mainProdId != null) return o
  const nested = o.data
  if (nested && typeof nested === 'object') return normalizeComboDetailPayload(nested)
  return null
}

/**
 * @description 从商品 VO 的 comboList 选取与当前主商品关联的套餐行（优先启用 + mainProdId 匹配）
 * @param {Object|null|undefined} product - ProductInfoVO / 搜索列表 VO
 * @returns {Object|null}
 */
export const pickComboRowForProduct = (product) => {
  if (!product || typeof product !== 'object') return null
  const list = product.comboList
  if (!Array.isArray(list) || list.length === 0) return null
  const pid = Number(product.prodId ?? product.id)
  if (!Number.isFinite(pid)) return list[0] || null
  return (
    list.find((c) => c && Number(c.mainProdId) === pid && Number(c.status) === 1) ||
    list.find((c) => c && Number(c.mainProdId) === pid) ||
    list[0] ||
    null
  )
}

/**
 * @description 从商品 VO 的 comboList 解析套餐活动 comboId（与首页搭配买、商详入口一致）
 * @param {Object|null|undefined} product
 * @returns {number|string|null}
 */
export const resolveComboIdFromProductVo = (product) => {
  const row = pickComboRowForProduct(product)
  const id = row?.comboId
  if (id === null || id === undefined || String(id).trim() === '') return null
  return id
}

/**
 * @description 从商详 VO 的 promotionType=3 / comboList 解析套餐 comboId
 * @param {Object|null|undefined} product
 * @returns {number|string|null}
 */
export const resolveComboIdFromPromotionProduct = (product) => {
  if (!product || typeof product !== 'object') return null
  const pt = Number(product.promotionType ?? 0)
  const raw = product.promotionId
  const n = Number(raw)
  if (pt === 3 && raw != null && raw !== '' && Number.isFinite(n) && n > 0) {
    return raw
  }
  return resolveComboIdFromProductVo(product)
}

/**
 * @description 商详是否应挂载「优惠套餐」模块（URL comboId / promotionType=3 / 未开始但 comboList 已挂活动）
 * @param {Object|null|undefined} product - 商详 ProductVO
 * @param {Object|null|undefined} [routeQuery] - 路由 query，含 comboId
 * @returns {boolean}
 */
export const shouldShowDetailComboPackage = (product, routeQuery) => {
  if (!product?.prodId && !product?.id) return false
  const q = routeQuery?.comboId ?? routeQuery?.promotionCode
  if (q !== undefined && q !== null && String(q).trim() !== '') return true

  const pt = Number(product.promotionType ?? 0)
  // 套餐类活动：搭配套餐(4)或组合促销(5)
  const raw = product.promotionId ?? product.promotionCode
  const n = Number(raw)
  if ((pt === 4 || pt === 5) && raw != null && raw !== '' && Number.isFinite(n) && n > 0)
    return true
  // 兼容旧数据（旧type=3→新type=4）
  if (pt === 3 && raw != null && raw !== '' && Number.isFinite(n) && n > 0) return true

  if (resolveComboIdFromProductVo(product) != null) return true

  return false
}

/** @description 套餐数量未返回 maxNum 时的默认上限 */
export const COMBO_PROD_QTY_FALLBACK_MAX = 99

/**
 * @description 套餐商品起搭/起购件数（`/combo/getComboByComboId` 的 `leastNum` 优先）
 * @param {Object|null|undefined} prod - mainProd / matchingProds 单项
 * @returns {number}
 */
/**
 * @description 套餐搭配品是否必选（`matchingProds[].required`）
 * @param {Object|null|undefined} prod
 * @returns {boolean}
 */
export const isComboMatchingProdRequired = (prod) => {
  if (!prod || typeof prod !== 'object') return false
  const raw = prod.required ?? prod.isRequired ?? prod.mustSelect ?? prod.mustBuy
  if (raw === true || raw === 1 || raw === '1') return true
  if (raw === false || raw === 0 || raw === '0') return false
  return false
}

/**
 * @description 套餐搭配品列表：必选项前置，组内保持原顺序
 * @param {Array<Object>} list
 * @returns {Array<Object>}
 */
export const sortComboMatchingProdsRequiredFirst = (list) => {
  if (!Array.isArray(list) || !list.length) return []
  return [...list]
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const ra = isComboMatchingProdRequired(a.item) ? 0 : 1
      const rb = isComboMatchingProdRequired(b.item) ? 0 : 1
      if (ra !== rb) return ra - rb
      return a.index - b.index
    })
    .map(({ item }) => item)
}

export const resolveComboProdLeastNum = (prod) => {
  if (!prod || typeof prod !== 'object') return 1
  const raw =
    prod.comboStartNum ??
    prod.combo_start_num ??
    prod.minPurchaseQty ??
    prod.startNum ??
    prod.leastNum ??
    prod.minBuyNum ??
    prod.minBuy ??
    prod.startSoldNum ??
    prod.comboMinNum ??
    prod.minNum ??
    1
  const n = Math.trunc(Number(raw))
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/**
 * @description 套餐商品最大购买件数（`maxNum` 优先）
 * @param {Object|null|undefined} prod - mainProd / matchingProds 单项
 * @returns {number}
 */
export const resolveComboProdMaxNum = (prod) => {
  if (!prod || typeof prod !== 'object') return COMBO_PROD_QTY_FALLBACK_MAX
  const raw =
    prod.comboMaxPurchaseQty ??
    prod.comboMaxNum ??
    prod.combo_max_num ??
    prod.maxNum ??
    prod.maxBuyNum ??
    prod.maxBuy ??
    prod.limitNum
  const n = Math.trunc(Number(raw))
  if (!Number.isFinite(n) || n < 1) return COMBO_PROD_QTY_FALLBACK_MAX
  return n
}

/**
 * @description 将套餐商品数量限制在 [leastNum, maxNum]（max 小于 least 时以 least 为有效上限）
 * @param {Object|null|undefined} prod - 商品 VO
 * @param {number|string} qty - 原始数量
 * @returns {number}
 */
export const clampComboProdQty = (prod, qty) => {
  const minQ = resolveComboProdLeastNum(prod)
  const maxQ = Math.max(minQ, resolveComboProdMaxNum(prod))
  const t = Math.trunc(Number(qty))
  const base = Number.isFinite(t) ? t : minQ
  return Math.min(maxQ, Math.max(minQ, base))
}

/**
 * @description 套餐起购件数；活动未进行中时按普通品视为 1
 * @param {Object|null|undefined} prod
 * @param {boolean} [activityInProgress=true]
 * @returns {number}
 */
export const resolveComboProdLeastNumWithActivity = (prod, activityInProgress = true) => {
  if (!activityInProgress) return 1
  return resolveComboProdLeastNum(prod)
}

/**
 * @description 套餐最大购买件数；活动未进行中时不套用活动限购
 * @param {Object|null|undefined} prod
 * @param {boolean} [activityInProgress=true]
 * @returns {number}
 */
export const resolveComboProdMaxNumWithActivity = (prod, activityInProgress = true) => {
  if (!activityInProgress) return COMBO_PROD_QTY_FALLBACK_MAX
  return resolveComboProdMaxNum(prod)
}

/**
 * @description 套餐数量夹紧；活动未进行中仅保证 ≥1
 * @param {Object|null|undefined} prod
 * @param {number|string} qty
 * @param {boolean} [activityInProgress=true]
 * @returns {number}
 */
export const clampComboProdQtyWithActivity = (prod, qty, activityInProgress = true) => {
  if (!activityInProgress) {
    const t = Math.trunc(Number(qty))
    const base = Number.isFinite(t) ? t : 1
    return Math.max(1, base)
  }
  return clampComboProdQty(prod, qty)
}

/**
 * @description 解析活动时间戳（毫秒）
 * @param {unknown} value
 * @returns {number}
 */
const pickActivityTimestamp = (value) => {
  const ts = dateToTimestamp(value)
  const n = typeof ts === 'number' ? ts : Number(ts)
  return Number.isFinite(n) && n > 0 ? n : 0
}

/**
 * @description 由起止原始值构造活动时间范围
 * @param {unknown} startRaw
 * @param {unknown} endRaw
 * @returns {{ startTime: number, endTime: number }|null}
 */
const buildActivityTimeRange = (startRaw, endRaw) => {
  const startTime = pickActivityTimestamp(startRaw)
  const endTime = pickActivityTimestamp(endRaw)
  if (startTime && endTime) return { startTime, endTime }
  return null
}

const SECKILL_START_TIME_KEYS = [
  'startTime',
  'startAt',
  'startDate',
  'startTimeStr',
  'activityStartTime',
  'activityInStartTime',
  'beginTime',
  'beginAt',
]

const SECKILL_END_TIME_KEYS = [
  'endTime',
  'endAt',
  'endDate',
  'endTimeStr',
  'activityEndTime',
  'activityFinishTime',
  'finishTime',
  'finishAt',
]

/**
 * @description 从秒杀 VO 指定字段列表解析时间戳
 * @param {Object|null|undefined} info
 * @param {string[]} keys
 * @returns {number}
 */
const getSeckillTimeFromKeys = (info, keys) => {
  if (!info || typeof info !== 'object') return 0
  for (const key of keys) {
    const ts = pickActivityTimestamp(info[key])
    if (ts) return ts
  }
  return 0
}

/**
 * @description 秒杀绝对起止时间（与商详 `DetailInfo.buildSeckillRange` 一致）
 * @param {Object|null|undefined} info
 * @returns {{ startTime: number, endTime: number }|null}
 */
const tryAbsoluteSeckillActivityRange = (info) => {
  if (!info || typeof info !== 'object') return null
  const startTime = getSeckillTimeFromKeys(info, SECKILL_START_TIME_KEYS)
  const endTime = getSeckillTimeFromKeys(info, SECKILL_END_TIME_KEYS)
  if (startTime && endTime) return { startTime, endTime }
  return null
}

/**
 * @description `/seckill/prod` 相对秒数字段拼倒计时区间（`startIn` / `expiresIn` / `activityStatus`）
 * @param {Object|null|undefined} info
 * @returns {{ startTime: number, endTime: number }|null}
 */
const buildSeckillRangeFromRelative = (info) => {
  if (!info || typeof info !== 'object') return null
  const now = Date.now()
  const activityStatus = Number(info.activityStatus)
  const expiresIn = Number(info.expiresIn)
  const startIn = Number(info.startIn)

  if (activityStatus === 3) return null

  if (activityStatus === 2 && Number.isFinite(expiresIn) && expiresIn > 0) {
    return { startTime: now - 1000, endTime: now + expiresIn * 1000 }
  }

  if (activityStatus === 1 && Number.isFinite(startIn) && startIn > 0) {
    const startTime = now + startIn * 1000
    let endTime = 0
    if (Number.isFinite(expiresIn) && expiresIn > 0) {
      const absEnd = now + expiresIn * 1000
      endTime = absEnd > startTime ? absEnd : startTime + expiresIn * 1000
    }
    if (!endTime || endTime <= startTime) {
      endTime = startTime + 24 * 3600 * 1000
    }
    return { startTime, endTime }
  }

  if (Number.isFinite(expiresIn) && expiresIn > 0) {
    return { startTime: now - 1000, endTime: now + expiresIn * 1000 }
  }

  return null
}

/**
 * @description 解析单条秒杀 VO 的活动时间范围
 * @param {Object|null|undefined} info
 * @returns {{ startTime: number, endTime: number }|null}
 */
export const resolveSeckillActivityTimeRange = (info) => {
  if (!info || typeof info !== 'object') return null
  const fromRoot = tryAbsoluteSeckillActivityRange(info)
  if (fromRoot) return fromRoot
  const nested = info.product
  if (nested && typeof nested === 'object') {
    const fromProduct = tryAbsoluteSeckillActivityRange(nested)
    if (fromProduct) return fromProduct
  }
  return buildSeckillRangeFromRelative(info)
}

/**
 * @description 限时折扣活动是否可透出（status 空 / 1 / 2）
 * @param {Object|null|undefined} d - getDiscountByProdId 单项
 * @returns {boolean}
 */
export const isMarkingDiscountVODisplayable = (d) => {
  if (!d || typeof d !== 'object') return false
  const s = d.status
  if (s === null || s === undefined || s === '') return true
  const n = Number(s)
  if (!Number.isFinite(n)) return true
  return n === 1 || n === 2
}

/**
 * @description 商详限时折扣：优先匹配 `promotionId`，否则取首个可展示活动
 * @param {Object|null|undefined} product
 * @returns {Object|null}
 */
export const pickMarkingDiscountForProduct = (product) => {
  if (!product || typeof product !== 'object') return null
  const list = Array.isArray(product.discounts) ? product.discounts : []
  if (!list.length) return null

  const pt = Number(product.promotionType ?? product.promotion_type ?? 0)
  const pid = Number(product.promotionId ?? product.promotion_id)
  if (pt === 2 && Number.isFinite(pid) && pid > 0) {
    const matched = list.find((d) => {
      const id = Number(d?.discountId ?? d?.id)
      return Number.isFinite(id) && id === pid
    })
    if (matched && isMarkingDiscountVODisplayable(matched)) return matched
  }

  return list.find((d) => isMarkingDiscountVODisplayable(d)) ?? null
}

/**
 * @description 买赠活动/赠品是否可参与加购（与商详横幅 status 规则一致）
 * @param {string|number|null|undefined} rawStatus
 * @returns {boolean}
 */
export const isGiveawayVODisplayable = (rawStatus) => {
  if (rawStatus === null || rawStatus === undefined || rawStatus === '') return true
  const status = Number(rawStatus)
  if (!Number.isFinite(status)) return true
  return status === 1 || status === 2
}

/**
 * @description 买赠活动起止时间
 * @param {Object|null|undefined} giveaway
 * @returns {{ startTime: number, endTime: number }|null}
 */
export const resolveGiveawayActivityTimeRange = (giveaway) => {
  if (!giveaway || typeof giveaway !== 'object') return null
  return buildActivityTimeRange(giveaway.startTime, giveaway.endTime)
}

/**
 * @description 从商品快照取买赠 VO（需活动态可展示）
 * @param {Object|null|undefined} product
 * @returns {Object|null}
 */
export const pickGiveawayForProduct = (product) => {
  if (!product || typeof product !== 'object') return null
  const giveaway = product.giveaway
  if (!giveaway || typeof giveaway !== 'object') return null
  if (!isGiveawayVODisplayable(giveaway.status)) return null
  return giveaway
}

/**
 * @description 解析买赠活动 VO（购物车行优先 `giveawayVO`，商详用 `giveaway`）
 * @param {Object|null|undefined} product
 * @returns {Object|null}
 */
export const resolveGiveawayVOFromSources = (product) => {
  if (!product || typeof product !== 'object') return null
  const raw = product.giveawayVO ?? product.giveaway
  if (raw && typeof raw === 'object') {
    return isGiveawayVODisplayable(raw.status) ? raw : null
  }
  return pickGiveawayForProduct(product)
}

/**
 * @description 解析限时折扣活动 VO（购物车行优先 `discountVO`，商详用 `discounts` 列表）
 * @param {Object|null|undefined} product
 * @returns {Object|null}
 */
export const resolveMarkingDiscountVOFromSources = (product) => {
  if (!product || typeof product !== 'object') return null
  const vo = product.discountVO
  if (vo && typeof vo === 'object') {
    return isMarkingDiscountVODisplayable(vo) ? vo : null
  }
  return pickMarkingDiscountForProduct(product)
}

/**
 * @description 限时折扣活动起止时间（绝对时间 + `startIn`/`expiresIn`/`activityStatus` 相对字段）
 * @param {Object|null|undefined} d - getDiscountByProdId 单项
 * @returns {{ startTime: number, endTime: number }|null}
 */
export const resolveMarkingDiscountActivityRange = (d) => {
  if (!d || typeof d !== 'object') return null
  const fromAbs = buildActivityTimeRange(
    d.startTime ?? d.beginTime ?? d.activityInStartTime ?? d.activityStartTime,
    d.endTime ?? d.finishTime ?? d.activityEndTime ?? d.activityFinishTime
  )
  if (fromAbs) return fromAbs
  return resolveSeckillActivityTimeRange(d)
}

/**
 * @description 从单份商品快照解析活动起止时间
 * @param {Object|null|undefined} product
 * @returns {{ startTime: number, endTime: number }|null}
 */
const resolveActivityTimeRangeFromProductSnapshot = (product) => {
  if (!product || typeof product !== 'object') return null

  const direct = buildActivityTimeRange(
    product.activityInStartTime ??
      product.startTime ??
      product.activityStartTime ??
      product.beginTime,
    product.activityEndTime ?? product.endTime ?? product.activityFinishTime ?? product.finishTime
  )
  if (direct) return direct

  const seckillCandidates = [
    product.seckillDetail,
    product.seckillVO,
    product.seckillSearchVO,
  ].filter(Boolean)
  for (const seckill of seckillCandidates) {
    const fromSeckill = resolveSeckillActivityTimeRange(seckill)
    if (fromSeckill) return fromSeckill
  }

  const comboRow = pickComboRowForProduct(product)
  if (comboRow) {
    const fromCombo = buildActivityTimeRange(comboRow.startTime, comboRow.endTime)
    if (fromCombo) return fromCombo
  }

  const markingDiscount = pickMarkingDiscountForProduct(product)
  if (markingDiscount) {
    const fromMarking = resolveMarkingDiscountActivityRange(markingDiscount)
    if (fromMarking) return fromMarking
  }

  const giveaway = pickGiveawayForProduct(product)
  if (giveaway) {
    const fromGiveaway = resolveGiveawayActivityTimeRange(giveaway)
    if (fromGiveaway) return fromGiveaway
  }

  return null
}

/**
 * @description 从商品快照解析活动起止时间（加购是否走活动维度）
 * @param {Object|null|undefined} product
 * @param {Object} [options]
 * @param {Array<Object>} [options.extraSources] - 额外数据源（如商详 store.productInfo）
 * @returns {{ startTime: number, endTime: number }|null}
 */
export const resolveProductActivityTimeRange = (product, options = {}) => {
  const extraSources = Array.isArray(options.extraSources) ? options.extraSources : []
  const sources = [product, ...extraSources].filter((src) => src && typeof src === 'object')
  for (const src of sources) {
    const range = resolveActivityTimeRangeFromProductSnapshot(src)
    if (range) return range
  }
  return null
}

/**
 * @description 活动倒计时阶段：0 未开始，1 进行中，2 已结束；无起止时间返回 null
 * @param {Object|null|undefined} product
 * @param {Object} [options]
 * @param {Array<Object>} [options.extraSources]
 * @returns {0|1|2|null}
 */
export const resolveProductActivityCountdownPhase = (product, options = {}) => {
  const range = resolveProductActivityTimeRange(product, options)
  if (!range) return null
  return getCountdown(range).type
}

/**
 * @description 商品活动是否进行中（加购/立即购买是否带活动维度）
 * @param {Object|null|undefined} product
 * @param {Object} [options]
 * @param {Array<Object>} [options.extraSources]
 * @returns {boolean}
 */
export const isStorePromotionActivityInProgress = (product, options = {}) => {
  const extraSources = Array.isArray(options.extraSources) ? options.extraSources : []
  const sources = [product, ...extraSources].filter((src) => src && typeof src === 'object')

  for (const src of sources) {
    const promotionDetail = src.promotionDetail
    if (promotionDetail && typeof promotionDetail === 'object') {
      if (isPromotionDetailInProgress(promotionDetail)) return true
      const range = resolvePromotionDetailTimeRange(promotionDetail)
      if (range && getCountdown(range).type === 1) return true
    }
    const phase = resolveProductActivityCountdownPhase(src, { extraSources: [] })
    if (phase !== null) return phase === 1

    const pt = Number(src.promotionType ?? src.promotion_type ?? 0)
    if (pt === 2) {
      const d = resolveMarkingDiscountVOFromSources(src)
      if (d) {
        const range = resolveMarkingDiscountActivityRange(d)
        if (range) return getCountdown(range).type === 1
        const pid = Number(src.promotionId ?? src.promotion_id)
        if (Number.isFinite(pid) && pid > 0 && isMarkingDiscountVODisplayable(d)) return true
      }
    }
    if (pt === PROMOTION_TYPE.BUY_GIFT || pt === 4) {
      const giveaway = resolveGiveawayVOFromSources(src)
      if (giveaway) {
        const range = resolveGiveawayActivityTimeRange(giveaway)
        if (range) return getCountdown(range).type === 1
        const gid = Number(
          src.promotionId ?? src.promotion_id ?? giveaway.giveawayId ?? giveaway.id
        )
        if (Number.isFinite(gid) && gid > 0) return true
      }
    }
    if (pt === PROMOTION_TYPE.VOLUME_PRICE && src.volumePriceActivity) {
      const range = resolvePromotionDetailTimeRange({
        startTime: src.volumePriceActivity.startTime,
        endTime: src.volumePriceActivity.endTime,
        displayStatus: src.volumePriceActivity.displayStatus,
      })
      if (range && getCountdown(range).type === 1) return true
    }
  }

  const pt = Number(product?.promotionType ?? product?.promotion_type ?? 0)
  if (pt === PROMOTION_TYPE.BUY_GIFT || pt === 4) {
    const giveaway = resolveGiveawayVOFromSources(product)
    if (giveaway) {
      const range = resolveGiveawayActivityTimeRange(giveaway)
      if (range) return getCountdown(range).type === 1
      const gid = Number(
        product.promotionId ?? product.promotion_id ?? giveaway.giveawayId ?? giveaway.id
      )
      if (Number.isFinite(gid) && gid > 0) return true
    }
  }
  if (pt > 0) return false
  return true
}

/**
 * @description 单行快照 `promotionStatus` 是否有效（`true` / `1` 为活动有效）
 * @param {Object|null|undefined} source
 * @returns {boolean}
 */
export const isStorePromotionLineStatusAvailable = (source) => {
  if (!source || typeof source !== 'object') return true
  const ps = source.promotionStatus ?? source.promotion_status
  if (ps === undefined || ps === null || ps === '') return true
  if (ps === true) return true
  if (ps === false) return false
  if (ps === 1 || ps === '1') return true
  if (ps === 0 || ps === '0') return false
  const pn = Number(ps)
  if (Number.isFinite(pn)) return pn === 1
  return true
}

/**
 * @description 单行快照 `promotionStatus` 是否不可用（与购物车行语义一致）
 * @param {Object|null|undefined} source
 * @returns {boolean}
 */
export const isStorePromotionLineStatusUnavailable = (source) => {
  return !isStorePromotionLineStatusAvailable(source)
}

/**
 * @description 套餐详情 VO `status` 是否不可用（非启用）
 * @param {Object|null|undefined} comboDetail
 * @returns {boolean}
 */
export const isComboDetailPromotionStatusUnavailable = (comboDetail) => {
  if (!comboDetail || typeof comboDetail !== 'object') return false
  const cs = Number(comboDetail.status)
  if (Number.isFinite(cs) && cs !== 1) return true
  return false
}

/**
 * @description 从商品/活动多数据源判断活动「状态」是否不可用（活动时间由 `isStorePromotionActivityInProgress` 判断）
 * @param {Object|null|undefined} product
 * @param {Array<Object>} [extraSources]
 * @returns {boolean}
 */
export const isStorePromotionStatusUnavailableFromSources = (product, extraSources = []) => {
  const sources = collectProductPromotionSources(product, extraSources)

  for (const src of sources) {
    if (isStorePromotionLineStatusUnavailable(src)) return true
  }

  const pt = resolvePromotionTypeFromSources(product, extraSources)
  if (!pt) return false

  if (pt === 3) {
    for (const src of sources) {
      if (isComboDetailPromotionStatusUnavailable(src.comboVO)) return true
      if (isComboDetailPromotionStatusUnavailable(src)) return true
      const list = Array.isArray(src.comboList) ? src.comboList : []
      const pid = Number(src.promotionId ?? src.promotion_id)
      if (Number.isFinite(pid) && pid > 0) {
        const matched = list.find((c) => Number(c?.comboId ?? c?.id) === pid)
        if (matched && isComboDetailPromotionStatusUnavailable(matched)) return true
      }
    }
    return false
  }

  if (pt === 1) {
    const seckill = resolveSeckillInfoFromSources(product, extraSources)
    if (!seckill) return false
    const ss = seckill.status ?? seckill.seckillStatus
    if (ss === false || ss === 0 || ss === '0') return true
    const sn = Number(ss)
    if (Number.isFinite(sn) && sn !== 1) return true
    return false
  }

  if (pt === 2) {
    for (const src of sources) {
      const d = resolveMarkingDiscountVOFromSources({
        ...src,
        promotionId: product?.promotionId ?? product?.promotion_id ?? src.promotionId,
        promotionType: product?.promotionType ?? product?.promotion_type ?? src.promotionType,
      })
      if (d && !isMarkingDiscountVODisplayable(d)) return true
    }
    return false
  }

  if (pt === 4) {
    for (const src of sources) {
      const g = resolveGiveawayVOFromSources(src)
      if (g && !isGiveawayVODisplayable(g.status)) return true
    }
  }

  return false
}

/**
 * @description 套餐详情 VO 活动是否进行中
 * @param {Object|null|undefined} comboDetail
 * @returns {boolean}
 */
/**
 * @description 格式化活动起止时间为展示用日期时间（YYYY-MM-DD HH:mm）
 * @param {*} raw
 * @returns {string}
 */
export const formatActivityDateLabel = (raw) => {
  if (raw === undefined || raw === null) return ''
  const s = String(raw).trim()
  if (!s) return ''
  const formatted = formatDate(raw, 'YYYY-MM-DD HH:mm')
  return formatted === EMPTY_VALUE_PLACEHOLDER ? s : formatted
}

/**
 * @description 套餐详情 VO 活动阶段：0 未开始，1 进行中，2 已结束；无起止时间返回 null
 * @param {Object|null|undefined} comboDetail
 * @returns {0|1|2|null}
 */
export const resolveComboDetailActivityPhase = (comboDetail) => {
  if (!comboDetail || typeof comboDetail !== 'object') return null
  const range = buildActivityTimeRange(
    comboDetail.startTime ??
      comboDetail.comboStartTime ??
      comboDetail.activityStartTime ??
      comboDetail.activityInStartTime,
    comboDetail.endTime ??
      comboDetail.comboEndTime ??
      comboDetail.activityEndTime ??
      comboDetail.activityFinishTime
  )
  if (!range) return null
  return getCountdown(range).type
}

export const isComboDetailActivityInProgress = (comboDetail) => {
  const phase = resolveComboDetailActivityPhase(comboDetail)
  if (phase === null) return true
  return phase === 1
}

/**
 * @description 套餐模块标题旁活动时间文案
 * @param {Object|null|undefined} comboDetail
 * @returns {{ prefix: string, date: string }|null}
 */
export const resolveComboActivityPeriodHeadline = (comboDetail) => {
  if (!comboDetail || typeof comboDetail !== 'object') return null
  const phase = resolveComboDetailActivityPhase(comboDetail)
  const startDate = formatActivityDateLabel(
    comboDetail.startTime ??
      comboDetail.comboStartTime ??
      comboDetail.activityStartTime ??
      comboDetail.activityInStartTime
  )
  const endDate = formatActivityDateLabel(
    comboDetail.endTime ??
      comboDetail.comboEndTime ??
      comboDetail.activityEndTime ??
      comboDetail.activityFinishTime
  )
  if (phase === 0) {
    return startDate ? { prefix: '活动开始', date: startDate } : null
  }
  if (phase === 1 || phase === 2) {
    return endDate ? { prefix: '活动截止', date: endDate } : null
  }
  return endDate ? { prefix: '活动截止', date: endDate } : null
}

/**
 * @description 非套餐加购是否应拦截（活动档期）
 * @reason 秒杀/满减/买赠未开始或已结束由 `normalizeAddToCartPromotionProduct` / `evaluateDetailAddToCartPromotion` 回退普通品；套餐未开始/已结束仅在 `DetailComboPackage` 拦截
 * @returns {null}
 */
export const resolveStorePromotionPeriodBlock = () => null

/**
 * @description 从商品快照定位 SKU 行（列表/PK 等无选中规格时取可解析或默认 SKU）
 * @param {Object|null|undefined} product
 * @returns {Object|null}
 */
export const resolveSkuSnapshotFromProduct = (product) => {
  if (!product || typeof product !== 'object') return null
  if (product.sku && typeof product.sku === 'object') return product.sku

  const skuList = Array.isArray(product.skuList) ? product.skuList : []
  const skuId = resolveProductSkuId(product)
  if (skuId != null && skuList.length) {
    const idStr = String(skuId)
    const matched = skuList.find((s) => {
      const raw = s?.skuId ?? s?.id ?? s?.prodSkuId
      return raw != null && String(raw) === idStr
    })
    if (matched) return matched
  }

  return resolveDefaultDetailSku(product)
}

/**
 * @description 合并主快照与 extraSources 为活动解析数据源列表
 * @param {Object|null|undefined} product
 * @param {Array<Object>} [extraSources]
 * @returns {Array<Object>}
 */
const collectProductPromotionSources = (product, extraSources = []) => {
  return [product, ...(Array.isArray(extraSources) ? extraSources : [])].filter(
    (src) => src && typeof src === 'object'
  )
}

/**
 * @description 从多份商品快照解析活动类型（含秒杀 VO 推断）
 * @param {Object|null|undefined} product
 * @param {Array<Object>} [extraSources]
 * @returns {number}
 */
export const resolvePromotionTypeFromSources = (product, extraSources = []) => {
  const sources = collectProductPromotionSources(product, extraSources)

  for (const src of sources) {
    const pt = Number(src.promotionType ?? src.promotion_type ?? 0)
    if (Number.isFinite(pt) && pt > 0) return Math.trunc(pt)
  }

  for (const src of sources) {
    const seckillVo = src.seckillDetail ?? src.seckillVO ?? src.seckillSearchVO
    if (seckillVo && typeof seckillVo === 'object') {
      const range = resolveSeckillActivityTimeRange(seckillVo)
      if (range && getCountdown(range).type === 1) return 1
    }
  }

  for (const src of sources) {
    const giveaway = pickGiveawayForProduct(src)
    if (giveaway) {
      const range = resolveGiveawayActivityTimeRange(giveaway)
      if (range && getCountdown(range).type === 1) return 4
    }
  }

  return 0
}

/**
 * @description 从多份商品快照解析买赠活动对象
 * @param {Object|null|undefined} product
 * @param {Array<Object>} [extraSources]
 * @returns {Object|null}
 */
export const resolveGiveawayInfoFromSources = (product, extraSources = []) => {
  const sources = collectProductPromotionSources(product, extraSources)
  for (const src of sources) {
    const giveaway = pickGiveawayForProduct(src)
    if (giveaway) return giveaway
  }
  return null
}

/**
 * @description 从多份商品快照解析秒杀活动对象
 * @param {Object|null|undefined} product
 * @param {Array<Object>} [extraSources]
 * @returns {Object|null}
 */
export const resolveSeckillInfoFromSources = (product, extraSources = []) => {
  const sources = collectProductPromotionSources(product, extraSources)

  for (const src of sources) {
    if (src?.seckillDetail && typeof src.seckillDetail === 'object') return src.seckillDetail
  }
  for (const src of sources) {
    if (src?.seckillVO && typeof src.seckillVO === 'object') return src.seckillVO
  }
  for (const src of sources) {
    if (src?.seckillSearchVO && typeof src.seckillSearchVO === 'object') return src.seckillSearchVO
  }

  return null
}

/**
 * @description 库存数字字段是否为显式非负值
 * @param {*} value
 * @returns {boolean}
 */
const isExplicitStockNumber = (value) => {
  if (value === null || value === undefined || value === '') return false
  const n = Number(value)
  return Number.isFinite(n) && n >= 0
}

/**
 * @description 仅以 `stocks` 读取 SKU 库存（不使用 stationStock / seckillStock 等）
 * @param {Object|null|undefined} item
 * @returns {number|null}
 */
export const pickSkuStocksField = (item) => {
  if (!item || typeof item !== 'object') return null
  if (!isExplicitStockNumber(item.stocks)) return null
  return Number(item.stocks)
}

/**
 * @typedef {Object} SeckillActivityStockState
 * @property {number} stock - 解析出的活动库存（不足时为 0）
 * @property {'sufficient'|'insufficient'|'unknown'} status - `unknown` 表示无法判定，应优先走活动加购
 */

/**
 * @description 按 `stocks` 解析库存状态
 * @param {number|null} stocks
 * @param {number} qty
 * @returns {SeckillActivityStockState|null}
 */
const resolveStockStateFromStocksField = (stocks, qty) => {
  if (stocks === null) return null
  if (stocks === 0) return { stock: 0, status: 'insufficient' }
  if (stocks >= qty) return { stock: stocks, status: 'sufficient' }
  return { stock: stocks, status: 'insufficient' }
}

/**
 * @description 秒杀活动 SKU 库存状态（仅以 `stocks` 判断；仅 `insufficient` 才应触发原价降级弹窗）
 * @param {Object|null|undefined} seckillInfo
 * @param {Object|null|undefined} currentSku
 * @param {number} [qty=1]
 * @returns {SeckillActivityStockState}
 */
export const resolveSeckillSkuActivityStockState = (seckillInfo, currentSku, qty = 1) => {
  const needQty = Math.max(1, Math.trunc(Number(qty) || 1))

  const resolveFromItem = (item) =>
    resolveStockStateFromStocksField(pickSkuStocksField(item), needQty)

  if (seckillInfo && typeof seckillInfo === 'object') {
    const currentSkuId = getSkuIdentityForStock(currentSku)
    const skuLists = [
      seckillInfo.seckillSkuList,
      seckillInfo.skuList,
      seckillInfo.skus,
      seckillInfo.seckillSkuVOList,
    ].filter((list) => Array.isArray(list) && list.length > 0)

    if (currentSkuId && skuLists.length > 0) {
      for (const list of skuLists) {
        const matched = list.find((sku) => getSkuIdentityForStock(sku) === currentSkuId)
        if (!matched) continue
        const fromMatched = resolveFromItem(matched)
        if (fromMatched) return fromMatched
        const fromCurrent = resolveFromItem(currentSku)
        if (fromCurrent) return fromCurrent
        return { stock: 0, status: 'unknown' }
      }
    }

    const fromInfo = resolveFromItem(seckillInfo)
    if (fromInfo) return fromInfo
  }

  const fromCurrentSku = resolveFromItem(currentSku)
  if (fromCurrentSku) return fromCurrentSku

  return { stock: 0, status: 'unknown' }
}

/**
 * @description 秒杀活动库存不足但 SKU 仍有库存时的回退确认决策（商详弹窗；仅活动库存「明确不足」时返回）
 * @param {Object} params
 * @param {Array<Object>} params.sources
 * @param {Object|null|undefined} params.currentSku
 * @param {number} params.qty
 * @param {number} params.promoCartQty
 * @param {number} params.normalStock
 * @returns {DetailAddToCartPromotionDecision|null}
 */
const buildSeckillActivityStockFallbackDecision = (params) => {
  const sources = Array.isArray(params.sources) ? params.sources : []
  const seckillInfo = resolveSeckillInfoFromSources(sources[0], sources.slice(1))
  if (!seckillInfo) return null

  const qty = Math.max(1, Math.trunc(Number(params.qty) || 1))
  const normalStock = Math.max(0, Number(params.normalStock) || 0)
  if (normalStock < qty) return null

  const stockState = resolveSeckillSkuActivityStockState(seckillInfo, params.currentSku, qty)
  const personMax = resolveSeckillPersonLimitMax(seckillInfo)
  const promoCartQty = Math.max(0, Math.trunc(Number(params.promoCartQty) || 0))
  const withinLimit = personMax <= 0 || promoCartQty + qty <= personMax
  const stockInsufficient = stockState.status === 'insufficient'

  if (stockState.status === 'sufficient' && withinLimit) return null
  if (stockState.status === 'unknown' && withinLimit) return null
  if (!stockInsufficient && withinLimit) return null

  const normalPayload = { promotionType: 0 }
  let confirmMessage = ''
  if (!withinLimit && stockInsufficient) {
    confirmMessage =
      personMax > 0
        ? `秒杀活动库存不足，且您已达到购买上限（限购${personMax}台），是否按原价继续购买？`
        : '秒杀活动库存不足，且您已达到购买上限，是否按原价继续购买？'
  } else if (!withinLimit) {
    confirmMessage =
      personMax > 0
        ? `您已达到该秒杀活动购买上限（限购${personMax}台），是否按原价继续购买？`
        : '您已达到该秒杀活动购买上限，是否按原价继续购买？'
  } else if (stockInsufficient) {
    confirmMessage = '秒杀活动库存不足，是否按原价继续购买？'
  } else {
    return null
  }

  return {
    action: 'normal',
    needConfirm: true,
    confirmMessage,
    payload: normalPayload,
  }
}

/**
 * @description 从商品快照推断加购校验用活动类型（不用于 changeItem/v3 提交；除套餐/组合专用入口外均按普通品加购）
 * @param {Object|null|undefined} product
 * @param {Object} [options]
 * @param {Array<Object>} [options.extraSources]
 * @param {Object|null} [options.currentSku]
 * @param {number} [options.quantity=1]
 * @param {boolean} [options.silentActivityStockFallback=true] - 活动库存不足但 SKU 仍有库存时是否静默回退普通品（商详决策传 false）
 * @returns {{ promotionType: number, promotionId?: number, comboKey?: string }}
 */
export const resolveProductPromotionForCart = (product, options = {}) => {
  const normal = { promotionType: 0 }
  if (!product || typeof product !== 'object') return normal

  const extraSources = Array.isArray(options.extraSources) ? options.extraSources : []
  if (!isStorePromotionActivityInProgress(product, { extraSources })) return normal
  if (isStorePromotionStatusUnavailableFromSources(product, extraSources)) return normal

  const pt = resolvePromotionTypeFromSources(product, extraSources)

  if (!pt) return normal

  // 自动匹配型：满量特价(1)、满量折扣(2)、买赠(3)不存储活动信息，购物车查询时动态匹配
  if (pt === 1 || pt === 2 || pt === 3) return normal

  const out = { promotionType: Math.trunc(pt) }

  // 存储型：搭配套餐(4)、组合促销(5)需要携带 promotionId
  if (pt === 4) {
    const giveaway = resolveGiveawayInfoFromSources(product, extraSources)
    const gidRaw =
      product.promotionId ?? product.promotion_id ?? giveaway?.giveawayId ?? giveaway?.id
    if (gidRaw != null && gidRaw !== '') {
      const n = Number(gidRaw)
      if (Number.isFinite(n) && n > 0) out.promotionId = n
    }
    return out
  }

  const pidRaw = product.promotionId ?? product.promotion_id
  if (pidRaw != null && pidRaw !== '') {
    const n = Number(pidRaw)
    if (Number.isFinite(n)) out.promotionId = n
  }

  return out
}

/**
 * @description 剥离加购载荷中的活动维度（满量特价/折扣/买赠等由后端自动匹配；套餐/组合走专用入口）
 * @param {Object|null|undefined} product
 * @returns {Object|null|undefined}
 */
export const stripActivityDimensionsForCartAdd = (product) => {
  if (!product || typeof product !== 'object') return product
  const {
    promotionId: _promotionId,
    promotion_id: _promotion_id,
    promotionCode: _promotionCode,
    promotion_code: _promotion_code,
    comboKey: _comboKey,
    combo_key: _combo_key,
    seckillId: _seckillId,
    seckill_id: _seckill_id,
    ...rest
  } = product
  return { ...rest, promotionType: 0 }
}

/**
 * @description 加购前归一化：通用入口一律按普通品提交（不传活动 id / 活动类型）
 * @param {Object|null|undefined} product
 * @param {Object} [options]
 * @returns {Object|null|undefined}
 */
export const normalizeAddToCartPromotionProduct = (product, options = {}) => {
  void options
  return stripActivityDimensionsForCartAdd(product)
}

/**
 * @description 快照是否含可判定的库存字段（列表/PK 等无库存字段时不应在前端拦截加购）
 * @param {Object|null|undefined} product
 * @param {Object|null|undefined} [currentSku]
 * @returns {boolean}
 */
export const hasExplicitProductStockForCart = (product, currentSku = null) => {
  const sku = currentSku ?? resolveSkuSnapshotFromProduct(product)
  if (sku?.inventoryStatus || product?.inventoryStatus) return true
  const candidates = [
    sku?.actualStock,
    sku?.actualStocks,
    sku?.warehouseStock,
    sku?.availableStock,
    sku?.stocks,
    sku?.stock,
    product?.totalStocks,
    product?.actualStocks,
    product?.actualStock,
    product?.stocks,
    product?.stock,
  ]
  return candidates.some((value) => isExplicitStockNumber(value))
}

/**
 * @description 从候选字段中取首个有效非负库存数
 * @param {Array<*>} candidates
 * @returns {number}
 */
const pickFirstNonNegativeStock = (candidates) => {
  for (const value of candidates) {
    if (value === null || value === undefined || value === '') continue
    const n = Number(value)
    if (Number.isFinite(n) && n >= 0) return n
  }
  return 0
}

/**
 * @description SKU 身份键（库存/秒杀 SKU 匹配）
 * @param {Object|null|undefined} sku
 * @returns {string}
 */
const getSkuIdentityForStock = (sku) => {
  if (!sku || typeof sku !== 'object') return ''
  const raw = sku.skuId ?? sku.id ?? sku.prodSkuId
  if (raw === null || raw === undefined || raw === '') return ''
  const n = Number(raw)
  return Number.isFinite(n) ? String(n) : String(raw).trim()
}

/**
 * @description 普通商品 SKU 库存（不含秒杀活动库存口径）
 * @param {Object|null|undefined} productInfo
 * @param {Object|null|undefined} currentSku
 * @returns {number}
 */
export const resolveNormalSkuStockFromProduct = (productInfo, currentSku) => {
  const inventory = resolveProductInventory(productInfo, currentSku)
  if (inventory.hasContract) return inventory.maxQuantity
  if (currentSku && typeof currentSku === 'object') {
    const fromSku = pickFirstNonNegativeStock([
      currentSku.actualStock,
      currentSku.actualStocks,
      currentSku.warehouseStock,
      currentSku.availableStock,
      currentSku.stocks,
      currentSku.stock,
    ])
    if (
      fromSku > 0 ||
      [
        currentSku.actualStock,
        currentSku.actualStocks,
        currentSku.warehouseStock,
        currentSku.availableStock,
        currentSku.stocks,
        currentSku.stock,
      ].some((value) => value != null && value !== '' && Number(value) === 0)
    )
      return fromSku
  }
  return pickFirstNonNegativeStock([
    productInfo?.totalStocks,
    productInfo?.actualStocks,
    productInfo?.actualStock,
    productInfo?.stocks,
    productInfo?.stock,
  ])
}

/**
 * @description 秒杀活动 SKU 库存（仅以 `stocks` 为准）
 * @param {Object|null|undefined} seckillInfo
 * @param {Object|null|undefined} currentSku
 * @returns {number}
 */
export const resolveSeckillSkuActivityStock = (seckillInfo, currentSku) => {
  const state = resolveSeckillSkuActivityStockState(seckillInfo, currentSku)
  if (state.status === 'unknown') return 0
  return state.stock
}

/**
 * @description 秒杀个人限购上限（`maxNum` 等；0 表示不限）
 * @param {Object|null|undefined} seckillInfo
 * @returns {number}
 */
export const resolveSeckillPersonLimitMax = (seckillInfo) => {
  if (!seckillInfo || typeof seckillInfo !== 'object') return 0
  const n = Number(
    seckillInfo.maxNum ?? seckillInfo.maxBuyNum ?? seckillInfo.maxBuy ?? seckillInfo.limitNum
  )
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0
}

/**
 * @typedef {Object} AddToCartStockValidation
 * @property {boolean} ok
 * @property {string} [message]
 * @property {boolean} [needConfirm] - 活动库存不足但商品仍有库存，需确认是否原价加购
 * @property {string} [confirmMessage]
 * @property {{ promotionType: number, promotionId?: number, comboKey?: string }} [fallbackPayload]
 */

/**
 * @description 加购前库存校验：秒杀等活动品校验活动库存与限购；不足且商品仍有库存时返回原价加购确认
 * @param {Object|null|undefined} product - 加购快照（含拟定的 promotionType）
 * @param {Object} [options]
 * @param {Array<Object>} [options.extraSources]
 * @param {Object|null} [options.currentSku]
 * @param {number} [options.quantity]
 * @param {number} [options.promoCartQty=0] - 购物车中同活动同 SKU 已加购数量
 * @param {boolean} [options.isAgencyUser=false] - 代理商不按库存数量拦截加购
 * @returns {AddToCartStockValidation}
 */
export const validateAddToCartStock = (product, options = {}) => {
  if (!product || typeof product !== 'object') {
    return { ok: false, message: '商品信息不完整' }
  }

  const extraSources = Array.isArray(options.extraSources)
    ? options.extraSources
    : Array.isArray(product.extraSources)
      ? product.extraSources
      : []
  const qty = Math.max(1, Math.trunc(Number(options.quantity ?? product.quantity) || 1))
  const currentSku = options.currentSku ?? resolveSkuSnapshotFromProduct(product)
  const pt = Number(product.promotionType ?? product.promotion_type ?? 0)
  const promoCartQty = Math.max(0, Math.trunc(Number(options.promoCartQty) || 0))
  const isAgencyUser = options.isAgencyUser === true || product.isAgencyUser === true
  const inventory = resolveProductInventory(product, currentSku, { isAgencyUser })
  if (inventory.hasContract) {
    if (inventory.blocked) return { ok: false, message: inventory.message }
    if (qty > inventory.maxQuantity)
      return { ok: false, message: `该规格只剩${inventory.maxQuantity}台` }
    // 库存快照只替代普通库存上限；秒杀仍需继续核对活动库存与限购。
    if (pt !== 1) return { ok: true }
  }

  if (pt === 1) {
    if (isAgencyUser) return { ok: true }
    const normalStock = resolveNormalSkuStockFromProduct(product, currentSku)
    const sources = collectProductPromotionSources(product, extraSources)
    const fallbackDecision = buildSeckillActivityStockFallbackDecision({
      sources,
      currentSku,
      qty,
      promoCartQty,
      normalStock,
    })
    if (fallbackDecision?.needConfirm) {
      return {
        ok: false,
        needConfirm: true,
        confirmMessage: fallbackDecision.confirmMessage,
        fallbackPayload: fallbackDecision.payload,
      }
    }

    const seckillInfo = resolveSeckillInfoFromSources(product, extraSources)
    const stockState = resolveSeckillSkuActivityStockState(seckillInfo, currentSku, qty)
    if (stockState.status === 'insufficient') {
      if (normalStock >= qty) {
        return {
          ok: false,
          needConfirm: true,
          confirmMessage: '秒杀活动库存不足，是否按原价继续购买？',
          fallbackPayload: { promotionType: 0 },
        }
      }
      return { ok: false, message: '秒杀活动库存不足' }
    }
    if (stockState.status === 'sufficient') {
      return { ok: true }
    }

    const fallbackStock = pickFirstNonNegativeStock([
      pickSkuStocksField(currentSku),
      product.stocks,
      product.totalStocks,
      seckillInfo?.stocks,
      seckillInfo?.seckillStock,
    ])
    if (fallbackStock < qty) {
      if (normalStock >= qty) {
        return {
          ok: false,
          needConfirm: true,
          confirmMessage: '秒杀活动库存不足，是否按原价继续购买？',
          fallbackPayload: { promotionType: 0 },
        }
      }
      return { ok: false, message: '秒杀活动库存不足' }
    }
    return { ok: true }
  }

  if (pt === 4) {
    if (isAgencyUser) return { ok: true }
    const normalStock = resolveNormalSkuStockFromProduct(product, currentSku)
    if (normalStock < qty) {
      if (!hasExplicitProductStockForCart(product, currentSku)) {
        return { ok: true }
      }
      return { ok: false, message: '该商品库存不足' }
    }
    return { ok: true }
  }

  const normalStock = resolveNormalSkuStockFromProduct(product, currentSku)
  if (isAgencyUser) return { ok: true }
  const fromField = pickFirstNonNegativeStock([
    pickSkuStocksField(currentSku),
    product.stocks,
    product.totalStocks,
    product.stock,
  ])
  const effective = currentSku ? normalStock : Math.max(normalStock, fromField)
  if (effective < qty) {
    if (!hasExplicitProductStockForCart(product, currentSku)) {
      return { ok: true }
    }
    return { ok: false, message: '该商品库存不足' }
  }
  return { ok: true }
}

/**
 * @typedef {Object} DetailAddToCartPromotionDecision
 * @property {'promotion'|'normal'|'unavailable'} action
 * @property {{ promotionType: number, promotionId?: number, comboKey?: string }} payload
 * @property {boolean} [needConfirm]
 * @property {string} [confirmMessage]
 */

/**
 * @description 加购/立即购买活动决策：活动库存与限购满足走活动；不足时若有 SKU 库存则回退普通品（仅 `confirmOnFallback` 为 true 时弹窗确认，商详使用）
 * @param {Object} context
 * @param {Object|null|undefined} context.product
 * @param {Array<Object>} [context.extraSources]
 * @param {Object|null|undefined} [context.currentSku]
 * @param {number} [context.quantity=1]
 * @param {number} [context.promoCartQty=0]
 * @param {boolean} [context.confirmOnFallback=false] - 仅商详为 true：活动库存不足但 SKU 仍有库存时弹窗确认原价加购
 * @param {boolean} [context.isAgencyUser=false] - 代理商不按库存数量拦截加购
 * @returns {DetailAddToCartPromotionDecision}
 */
export const evaluateDetailAddToCartPromotion = (context = {}) => {
  const product = context.product
  const extraSources = Array.isArray(context.extraSources) ? context.extraSources : []
  const sources = [product, ...extraSources].filter((src) => src && typeof src === 'object')
  const snap = product || sources[0] || {}
  const currentSku = context.currentSku
  const qty = Math.max(1, Math.trunc(Number(context.quantity) || 1))
  const promoCartQty = Math.max(0, Math.trunc(Number(context.promoCartQty) || 0))
  const confirmOnFallback = context.confirmOnFallback === true
  const isAgencyUser = context.isAgencyUser === true

  const normalPayload = { promotionType: 0 }
  const inventory = resolveProductInventory(snap, currentSku, { isAgencyUser })
  if (inventory.updating)
    return { action: 'blocked', message: inventory.message, payload: normalPayload }
  const normalStock = inventory.hasContract
    ? inventory.maxQuantity
    : resolveNormalSkuStockFromProduct(snap, currentSku)

  if (!isAgencyUser && normalStock < qty) {
    return { action: 'unavailable', payload: normalPayload }
  }

  if (!isStorePromotionActivityInProgress(snap, { extraSources: sources.slice(1) })) {
    return { action: 'normal', payload: normalPayload }
  }

  if (isStorePromotionStatusUnavailableFromSources(snap, sources.slice(1))) {
    return { action: 'normal', payload: normalPayload }
  }

  const pt = Number(snap.promotionType ?? snap.promotion_type ?? 0)
  if (pt === 3) {
    return { action: 'normal', payload: normalPayload }
  }

  let promotionPayload = resolveProductPromotionForCart(snap, {
    extraSources: sources.slice(1),
    currentSku,
    quantity: qty,
    silentActivityStockFallback: !confirmOnFallback,
  })

  if (
    promotionPayload.promotionType === 0 &&
    !isStorePromotionStatusUnavailableFromSources(snap, sources.slice(1))
  ) {
    const inferredPt = resolvePromotionTypeFromSources(snap, sources.slice(1))
    // 仅存储型活动(4/5)需要回退推断；自动匹配型(1/2/3)不携带活动信息
    if (inferredPt === 4) {
      const giveaway = resolveGiveawayInfoFromSources(snap, sources.slice(1))
      const gidRaw = snap.promotionId ?? snap.promotion_id ?? giveaway?.giveawayId ?? giveaway?.id
      promotionPayload = { promotionType: 4 }
      const gid = Number(gidRaw)
      if (Number.isFinite(gid) && gid > 0) promotionPayload.promotionId = gid
    }
  }

  if (promotionPayload.promotionType === 0) {
    return { action: 'normal', payload: normalPayload }
  }

  // 秒杀(旧1)已废弃；满量特价(新1)自动匹配，不需要特殊处理
  // 买赠(新3)需要校验赠品库存
  if (promotionPayload.promotionType === 3) {
    // 买赠活动处理（原type=4→新type=3）
    // TODO: 如果需要买赠活动库存校验，在此添加
    return { action: 'promotion', payload: promotionPayload }
  }

  return { action: 'promotion', payload: promotionPayload }
}

/**
 * @description 列表/搜索快照是否套餐活动商品（加购应按普通品，不传套餐活动维度）
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export const isComboActivityListProduct = (product) => {
  if (!product || typeof product !== 'object') return false
  // 套餐类活动：搭配套餐(4)或组合促销(5)
  const pt = Number(product.promotionType)
  if (pt === 4 || pt === 5) return true
  // 兼容旧数据（旧type=3→新type=4）
  if (pt === 3) return true
  const list = product.comboList
  return Array.isArray(list) && list.length > 0
}

/**
 * @description 商品列表/搜索列表加购载荷：不传活动 id / 活动类型（套餐/组合须走专用入口）
 * @param {Object|null|undefined} product - 列表 VO 快照
 * @returns {Object|null|undefined}
 */
export const buildListAddToCartPayload = (product) => {
  if (!product || typeof product !== 'object') return product
  const normalized = normalizeAddToCartPromotionProduct(product)
  const skuId = resolvePriceSkuId(normalized)

  return {
    ...normalized,
    ...(skuId ? { skuId } : {}),
  }
}

/**
 * @description 列表快捷加购保留价格默认规格，仅在其明确售罄时改选有货规格。
 * @param {Object} product 包含最新 skuList 的商品快照
 * @param {{isAgencyUser?: boolean}} options 身份库存豁免
 * @returns {Object|null} 选中规格；没有有货候选时保留默认，交给库存校验拦截
 */
export const resolveListAddToCartSku = (product, options = {}) => {
  const skus = (Array.isArray(product?.skuList) ? product.skuList : []).filter(
    (sku) => sku && (sku.status == null || Number(sku.status) === 1)
  )
  const defaultId = resolvePriceSkuId(product) || resolveProductSkuId(product)
  const defaultSku =
    skus.find((sku) => getSkuIdentityForStock(sku) === String(defaultId)) || skus[0] || null
  if (!defaultSku || options.isAgencyUser) return defaultSku
  if (!resolveProductInventory(null, defaultSku).soldOut) return defaultSku
  return (
    skus.find((sku) => {
      const inventory = resolveProductInventory(null, sku)
      return !inventory.blocked && inventory.availableQuantity > 0
    }) || defaultSku
  )
}

/**
 * @description 订单行活动编码（兼容 promotionCode / promotionId / comboVO）
 * @param {Object|null|undefined} row
 * @returns {string}
 */
const resolveOrderItemPromotionCode = (row) => {
  if (!row || typeof row !== 'object') return ''
  const raw =
    row.promotionCode ??
    row.promotion_code ??
    row.promotionId ??
    row.promotion_id ??
    row.comboVO?.comboId ??
    row.comboId
  if (raw == null || raw === '') return ''
  return String(raw).trim()
}

/**
 * @description 订单行活动类型（兼容旧版 type=3 套餐）
 * @param {Object|null|undefined} row
 * @returns {number}
 */
const resolveOrderItemPromotionType = (row) => {
  const raw = Number(row?.promotionType ?? row?.promotion_type ?? 0)
  if (!Number.isFinite(raw)) return PROMOTION_TYPE.NONE
  const mapped = mapLegacyPromotionType(raw)
  return mapped ?? raw
}

/**
 * @description 再次购买 → changeItem/v3 入参：普通品不带活动维度；搭配套餐/组合促销保留活动类型与活动编码
 * @param {Array<Object>} orderItems - order.orderItemDtos / order.products
 * @returns {Array<Object>}
 */
export const buildBuyAgainCartChangeItems = (orderItems) => {
  const source = Array.isArray(orderItems) ? orderItems : []
  const rows = source
    .map((item) => {
      const prodId = item.prodId ?? item.productId ?? item.productVO?.prodId
      const skuId = resolveProductSkuId(item)
      const basketCount = Math.max(
        1,
        Math.trunc(Number(item.prodCount ?? item.count ?? item.quantity ?? 1))
      )
      const promotionType = resolveOrderItemPromotionType(item)
      const promotionCode = resolveOrderItemPromotionCode(item)
      const comboKey = item.comboKey ?? item.combo_key ?? ''
      return {
        prodId,
        skuId,
        basketCount,
        promotionType,
        promotionCode,
        comboItemType: item.comboItemType ?? item.combo_item_type,
        groupKey: comboPackageGroupKeyFromRow({
          promotionType,
          promotionCode,
          promotionId: promotionCode,
          comboKey,
        }),
      }
    })
    .filter((row) => row.skuId != null && row.prodId != null)

  const payloads = []
  const consumed = new Set()

  for (let i = 0; i < rows.length; i++) {
    if (consumed.has(i)) continue
    const row = rows[i]

    if (row.promotionType === PROMOTION_TYPE.COMBO_PROMO && row.promotionCode && row.groupKey) {
      const groupIndices = []
      for (let j = 0; j < rows.length; j++) {
        if (consumed.has(j)) continue
        if (rows[j].groupKey === row.groupKey) groupIndices.push(j)
      }
      groupIndices.forEach((idx) => consumed.add(idx))
      payloads.push({
        promotionType: PROMOTION_TYPE.COMBO_PROMO,
        promotionCode: row.promotionCode,
        comboItems: groupIndices.map((idx) => ({
          skuId: rows[idx].skuId,
          prodCount: rows[idx].basketCount,
        })),
        isChecked: 1,
      })
      continue
    }

    if (row.promotionType === PROMOTION_TYPE.COMBO_BUNDLE && row.promotionCode && row.groupKey) {
      const groupIndices = []
      for (let j = 0; j < rows.length; j++) {
        if (consumed.has(j)) continue
        if (rows[j].groupKey === row.groupKey) groupIndices.push(j)
      }
      groupIndices.forEach((idx) => consumed.add(idx))
      payloads.push({
        promotionType: PROMOTION_TYPE.COMBO_BUNDLE,
        promotionCode: row.promotionCode,
        comboItems: groupIndices.map((idx) => {
          const line = rows[idx]
          const item = {
            skuId: line.skuId,
            prodCount: line.basketCount,
          }
          const comboItemType = Number(line.comboItemType)
          if (Number.isFinite(comboItemType) && comboItemType > 0) {
            item.comboItemType = comboItemType
          }
          return item
        }),
        isChecked: 1,
      })
      continue
    }

    consumed.add(i)
    payloads.push({
      prodId: row.prodId,
      skuId: row.skuId,
      basketCount: row.basketCount,
      isChecked: 1,
      promotionType: PROMOTION_TYPE.NONE,
    })
  }

  return payloads
}

/**
 * @description 商详默认 SKU（启用态优先）
 * @param {Object|null|undefined} detail - `/p/store/product/info` 返回（原 mall4j `/prod/prodInfo` 契约）
 * @returns {Object|null}
 */
export const resolveDefaultDetailSku = (detail) => {
  const skuList = Array.isArray(detail?.skuList) ? detail.skuList : []
  return skuList.find((s) => s?.status === 1) || skuList[0] || null
}

const resolvePriceSkuId = (product) => {
  if (!product || typeof product !== 'object') return ''
  const value = product.priceSkuId
  if (value === null || value === undefined || value === '') return ''
  return String(value).trim()
}

/**
 * @description 拉取商详并补全新营销活动数据（不写 Pinia，供 PK/列表快捷加购等）
 * @param {string|number} prodId
 * @returns {Promise<{ detail: Object|null, extraSources: Array<Object> }>}
 */
export const fetchProductDetailForCartPromotion = async (prodId) => {
  const numericId = Number(prodId)
  if (!Number.isFinite(numericId) || numericId <= 0) {
    return { detail: null, extraSources: [] }
  }

  try {
    const detail = await productApi.getProductDetail({
      prodId: numericId,
      addrId: 0,
      dvyType: 1,
      needPointStock: false,
    })

    if (!detail || typeof detail !== 'object' || detail.success === false) {
      return { detail: null, extraSources: [] }
    }

    const extraSources = []
    const requestPlan = resolvePromotionDetailRequest({
      productSnapshot: detail,
      productId: numericId,
    })

    if (requestPlan) {
      try {
        let raw = null
        if (requestPlan.mode === 'by-id') {
          raw = await storeApi.getPromotionDetail(requestPlan.promotionId)
        } else {
          raw = await storeApi.getPromotionByProduct(requestPlan.productId)
        }
        const promotionDetail = unwrapPromotionDetail(raw)
        if (promotionDetail) {
          Object.assign(detail, mapPromotionDetailToProductPatch(promotionDetail, numericId))
          extraSources.push(promotionDetail)
        }
      } catch {
        // 活动详情失败时仍返回商详本体
      }
    }

    return { detail, extraSources }
  } catch {
    return { detail: null, extraSources: [] }
  }
}

/**
 * @description PK 对比行与商详合并为加购快照（默认 SKU、库存）
 * @param {Object|null|undefined} pkProduct - 对比接口 product 行
 * @param {Object} detail - 商详 VO
 * @param {Object|null} [currentSku] - 当前 SKU
 * @returns {Object}
 */
export const buildPkProductSnapshotForCart = (pkProduct, detail, currentSku = null) => {
  const sku = currentSku || resolveDefaultDetailSku(detail)
  const prodId = detail.prodId ?? detail.id ?? pkProduct?.id
  const skuId =
    resolveProductSkuId({
      ...detail,
      skuId: sku?.skuId ?? sku?.id ?? sku?.prodSkuId,
    }) ?? resolveProductSkuId(pkProduct)
  const stocks = resolveNormalSkuStockFromProduct(detail, sku)

  return {
    ...(pkProduct && typeof pkProduct === 'object' ? pkProduct : {}),
    ...detail,
    id: prodId,
    prodId,
    skuId,
    quantity: 1,
    stocks,
    totalStocks: stocks,
  }
}

/**
 * @description 购物车中同活动同 SKU 已加购数量（秒杀限购等）
 * @param {Array<Object>} cartItems
 * @param {Object} product - 商详或合并快照
 * @param {Object} [options]
 * @param {Object|null} [options.currentSku]
 * @param {{ promotionType: number, promotionId?: number }} [options.promotionPayload]
 * @returns {number}
 */
export const resolvePromoCartQtyInCart = (cartItems, product, options = {}) => {
  const items = Array.isArray(cartItems) ? cartItems : []
  const pt = Number(
    options.promotionPayload?.promotionType ??
      product?.promotionType ??
      product?.promotion_type ??
      0
  )
  if (!pt) return 0

  const prodId = Number(product?.prodId ?? product?.id)
  const skuId = resolveProductSkuId({
    ...product,
    skuId:
      options.currentSku?.skuId ??
      options.currentSku?.id ??
      options.currentSku?.prodSkuId ??
      product?.skuId,
  })
  if (!Number.isFinite(prodId) || prodId <= 0 || skuId == null) return 0

  const pid = Number(
    options.promotionPayload?.promotionId ?? product?.promotionId ?? product?.promotion_id
  )
  const sid = String(skuId)

  return items.reduce((sum, item) => {
    if (Number(item.prodId) !== prodId) return sum
    if (String(resolveProductSkuId(item) ?? item.skuId) !== sid) return sum
    if (Number(item.promotionType) !== pt) return sum
    if (Number.isFinite(pid) && pid > 0 && Number(item.promotionId) !== pid) return sum
    return sum + Math.max(0, Math.trunc(Number(item.quantity) || 0))
  }, 0)
}

/**
 * @description PK 对比页加购载荷：套餐仍剥离活动维度；非套餐活动品按商详活动加购
 * @param {Object|null|undefined} pkProduct
 * @param {Object} detail
 * @param {{ payload: { promotionType: number, promotionId?: number, comboKey?: string } }} decision
 * @param {Object} [options]
 * @param {Object|null} [options.currentSku]
 * @param {Array<Object>} [options.extraSources]
 * @returns {Object}
 */
export const buildPkAddToCartPayload = (pkProduct, detail, decision, options = {}) => {
  const snapshot = buildPkProductSnapshotForCart(pkProduct, detail, options.currentSku)
  if (isComboActivityListProduct(detail)) {
    return buildListAddToCartPayload(snapshot)
  }
  const extraSources = Array.isArray(options.extraSources) ? options.extraSources : []
  return {
    ...snapshot,
    ...decision.payload,
    ...(extraSources.length ? { extraSources } : {}),
  }
}

/**
 * @description 从商品快照（列表、收藏、PK、方案配单、订单行等）解析加购/改购物车用的 SKU id
 * @param {Object|null|undefined} product - 商品或含 sku 字段的快照；列表快捷加购应先由 buildListAddToCartPayload 写入 skuId
 * @returns {number|string|null}
 */
export const resolveProductSkuId = (product) => {
  if (!product || typeof product !== 'object') return null
  const nested = product.sku
  const fromSkuIds =
    Array.isArray(product.skuIds) && product.skuIds.length > 0 ? product.skuIds[0] : null
  const fromSkuIdList =
    Array.isArray(product.skuIdList) && product.skuIdList.length > 0 ? product.skuIdList[0] : null
  const raw =
    product.skuId ??
    fromSkuIds ??
    fromSkuIdList ??
    product.defaultSkuId ??
    product.prodSkuId ??
    (nested && typeof nested === 'object' ? (nested.skuId ?? nested.id ?? nested.prodSkuId) : null)
  if (raw === null || raw === undefined || raw === '') return null
  const n = Number(raw)
  if (Number.isFinite(n) && n > 0) return n
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (!t) return null
    const n2 = Number(t)
    if (Number.isFinite(n2) && n2 > 0) return n2
  }
  return null
}

/**
 * @description 根据产品介绍图片开关解析展示 URL：始终保留 1280 限宽，按开关决定是否追加 webp 格式后缀。
 * @param {string|Array|Record<string, any>} url - 产品介绍图片原始地址，支持字符串、数组或对象结构。
 * @param {boolean} [useOriginalImage=true] - 是否去掉格式后缀并直接使用原图格式。
 * @returns {string} 产品介绍图片展示地址。
 */
export const resolveStoreProductIntroImageUrl = (url, useOriginalImage = true) => {
  const originUrl = getOssOriginUrl(url)
  if (!originUrl) return ''
  const limitedUrl = getOssLimitedWidthUrl(originUrl)
  if (useOriginalImage) return limitedUrl
  return appendOssImageProcess(limitedUrl, STORE_PRODUCT_INTRO_WEBP_PROCESS)
}

/**
 * @description 解析 SKU 规格字符串 "p1:v1;p2:v2" 为对象
 * @param {string} properties
 * @returns {Record<string, string>}
 */
export const parseSkuProperties = (properties) => {
  if (!properties || typeof properties !== 'string') return {}
  return properties
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .reduce((acc, pair) => {
      const idx = pair.indexOf(':')
      if (idx <= 0) return acc
      const key = pair.slice(0, idx).trim()
      const val = pair.slice(idx + 1).trim()
      if (!key || !val) return acc
      acc[key] = val
      return acc
    }, {})
}

/**
 * @description 从对象中提取价格字段
 * @param {Object} target - 目标对象
 * @param {string[]} keys - 价格字段列表
 * @returns {number|null} 价格
 */
export const pickPriceValue = (target, keys) => {
  if (!target) return null
  for (const key of keys) {
    const value = target[key]
    if (value === null || value === undefined || value === '') continue
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

/**
 * @description 归一化商品详情中的方案 ID 列表
 * @reason 后端字段 `solution` 可能为数组/JSON字符串/逗号分隔字符串，统一转为字符串数组
 * @param {unknown} raw - 原始方案字段
 * @returns {string[]} 方案 ID 字符串数组
 */
export const normalizeSolutionIds = (raw) => {
  if (!raw) return []

  const ensureStringArray = (value) => {
    if (!Array.isArray(value)) return []
    const ids = value
      .map((item) => (item === null || item === undefined ? '' : String(item).trim()))
      .filter(Boolean)
    // 去重且保持顺序
    return Array.from(new Set(ids))
  }

  if (Array.isArray(raw)) {
    // 详情接口返回：方案id字符串数组
    return ensureStringArray(raw)
  }

  if (typeof raw === 'string') {
    const text = raw.trim()
    if (!text) return []

    if (text.startsWith('[')) {
      try {
        return ensureStringArray(JSON.parse(text))
      } catch (err) {
        return ensureStringArray(text.split(','))
      }
    }

    return ensureStringArray(text.split(','))
  }

  return []
}

/**
 * @description 计算商品显示价格 (营销活动价、多规格区间或单价)
 * @param {Object} productInfo - 商品信息
 * @param {Object|null} [currentSku] - 当前选中的 SKU
 * @returns {string} 格式化后的价格字符串
 */
export const calculateDisplayPrice = (productInfo, currentSku = null) => {
  if (!productInfo) return '0.00'

  if (isPromotionDetailCatalogPriceType(productInfo?.promotionDetail)) {
    const catalogPrice = resolvePromotionDetailCatalogPrice(productInfo, currentSku)
    if (catalogPrice !== null && catalogPrice > 0) {
      return formatPrice(catalogPrice)
    }
  }

  const promotionPriceInfo = getPromotionDetailPriceInfo(productInfo, currentSku)
  if (promotionPriceInfo && Number(promotionPriceInfo.price) > 0) {
    return formatPrice(promotionPriceInfo.price)
  }

  // 业务价由后端按登录身份(角色/等级)解析后统一以 price 返回（未登录/无匹配取终端客户价）。
  const priceKeys = ['price']

  // 如果有选中完整的 SKU，显示该 SKU 的价格
  const skuPrice = pickPriceValue(currentSku, priceKeys)
  if (skuPrice !== null) return formatPrice(skuPrice)

  // 未选中完整 SKU，显示价格区间
  if (productInfo.skuList && productInfo.skuList.length > 0) {
    const prices = productInfo.skuList
      .filter((s) => s.status === 1) // 只统计启用的 SKU
      .map((s) => pickPriceValue(s, priceKeys) ?? 0)
      .filter((p) => p > 0)
    if (prices.length === 0) return '0.00'
    const min = Math.min(...prices)
    const max = Math.max(...prices)
    if (min === max) return formatPrice(min)
    return `${formatPrice(min)} - ${formatPrice(max)}`
  }

  const singlePrice = pickPriceValue(productInfo, priceKeys) ?? 0
  return formatPrice(singlePrice)
}

/**
 * @description 计算商品划线价 (原价)
 * @param {Object} productInfo - 商品信息
 * @param {Object|null} [currentSku] - 当前选中的 SKU
 * @returns {string|null} 格式化后的价格字符串或 null
 */
export const calculateOriginalPrice = (productInfo, currentSku = null) => {
  if (!productInfo) return null

  if (isPromotionDetailCatalogPriceType(productInfo?.promotionDetail)) {
    return null
  }

  const promotionPriceInfo = getPromotionDetailPriceInfo(productInfo, currentSku)
  if (
    promotionPriceInfo &&
    promotionPriceInfo.originalPrice != null &&
    Number(promotionPriceInfo.originalPrice) > Number(promotionPriceInfo.price ?? 0)
  ) {
    return formatPrice(promotionPriceInfo.originalPrice)
  }

  // 后端商品接口不再返回划线价(原价)；非促销无原价，仅秒杀/促销有 originalPrice（见上方分支）。
  const oriPriceKeys = ['oriPrice']

  // 如果有选中完整的 SKU，显示该 SKU 的原价
  const skuOriPrice = pickPriceValue(currentSku, oriPriceKeys)
  if (skuOriPrice && skuOriPrice > 0) return formatPrice(skuOriPrice)

  // 未选中完整 SKU，显示最高原价
  if (productInfo.skuList && productInfo.skuList.length > 0) {
    const prices = productInfo.skuList
      .filter((s) => s.status === 1)
      .map((s) => pickPriceValue(s, oriPriceKeys) ?? 0)
      .filter((p) => p > 0)
    if (prices.length === 0) return null
    return formatPrice(Math.max(...prices))
  }

  const singleOriPrice = pickPriceValue(productInfo, oriPriceKeys) ?? 0
  return singleOriPrice > 0 ? formatPrice(singleOriPrice) : null
}

/**
 * @description 归一化折扣率为 (0,1] 小数（兼容 0.9 / 9 / 90）
 * @param {number|string} raw
 * @returns {number|null}
 */
function normalizePromotionDiscountRate(raw) {
  const num = Number(raw)
  if (!Number.isFinite(num) || num <= 0) return null
  if (num <= 1) return num
  if (num <= 10) return num / 10
  if (num <= 100) return num / 100
  return null
}

/**
 * @description 解析商详底栏可用的满量特价/折扣阶梯
 * @param {Object|null|undefined} productInfo
 * @returns {{ promotionType: number, hierarchies: Array<Object> }|null}
 */
function resolveDetailBottomBarPromotionHierarchies(productInfo) {
  if (!productInfo || typeof productInfo !== 'object') return null

  const detail = productInfo.promotionDetail
  const detailType = Number(detail?.promotionType ?? detail?.activityType)
  if (
    detail &&
    isPromotionDetailInProgress(detail) &&
    (detailType === PROMOTION_TYPE.VOLUME_PRICE || detailType === PROMOTION_TYPE.VOLUME_DISCOUNT)
  ) {
    const productRow = pickPromotionDetailProduct(detail, productInfo.prodId)
    const tiers = Array.isArray(productRow?.tiers) ? productRow.tiers : []
    if (tiers.length) return { promotionType: detailType, hierarchies: tiers }
  }

  const volumeTiers = Array.isArray(productInfo.volumePriceActivity?.tiers)
    ? productInfo.volumePriceActivity.tiers
    : []
  if (volumeTiers.length) {
    return { promotionType: PROMOTION_TYPE.VOLUME_PRICE, hierarchies: volumeTiers }
  }

  const markingDiscount = resolveMarkingDiscountVOFromSources(productInfo)
  if (markingDiscount && isMarkingDiscountVODisplayable(markingDiscount)) {
    const rule = Number(markingDiscount.discountRule)
    const items = Array.isArray(markingDiscount.discountItems) ? markingDiscount.discountItems : []
    if (items.length) {
      const hierarchies = items.map((item) => ({
        buyNum: item.needAmount ?? item.needNum ?? item.fullPiece ?? item.piece,
        promotionPrice:
          item.promotionPrice ??
          item.discountPrice ??
          item.activityPrice ??
          item.specialPrice ??
          item.unitPrice,
        promotionDiscount: item.discount ?? item.discountValue,
        discount: item.discount ?? item.discountValue,
      }))
      const isDiscountRate =
        rule === 3 ||
        hierarchies.some((tier) => {
          const rate = normalizePromotionDiscountRate(tier.promotionDiscount)
          return rate !== null && rate > 0 && rate < 1
        })
      return {
        promotionType: isDiscountRate
          ? PROMOTION_TYPE.VOLUME_DISCOUNT
          : PROMOTION_TYPE.VOLUME_PRICE,
        hierarchies,
      }
    }
  }

  return null
}

/**
 * @description 按购买数量解析已生效的促销单价（仅满量特价/折扣）
 * @param {Object|null|undefined} productInfo
 * @param {number} quantity - 台数
 * @param {number} catalogPrice - 价目表单价
 * @returns {number|null}
 */
function resolveDetailQuantityPromoUnitPrice(productInfo, quantity, catalogPrice) {
  const ctx = resolveDetailBottomBarPromotionHierarchies(productInfo)
  if (!ctx || !Array.isArray(ctx.hierarchies) || ctx.hierarchies.length === 0) return null

  const appliedTier = selectAppliedTier(ctx.hierarchies, quantity)
  if (!appliedTier) return null

  if (ctx.promotionType === PROMOTION_TYPE.VOLUME_PRICE) {
    const promoPrice = Number(
      appliedTier.promotionPrice ??
        appliedTier.discountPrice ??
        appliedTier.activityPrice ??
        appliedTier.specialPrice
    )
    if (Number.isFinite(promoPrice) && promoPrice > 0) return promoPrice
  }

  if (ctx.promotionType === PROMOTION_TYPE.VOLUME_DISCOUNT) {
    const rate = normalizePromotionDiscountRate(
      appliedTier.promotionDiscount ?? appliedTier.discount ?? appliedTier.discountValue
    )
    if (rate !== null && rate > 0 && rate < 1 && catalogPrice > 0) {
      return catalogPrice * rate
    }
  }

  const fallbackPromo = Number(
    appliedTier.promotionPrice ?? appliedTier.discountPrice ?? appliedTier.activityPrice
  )
  if (Number.isFinite(fallbackPromo) && fallbackPromo > 0) return fallbackPromo

  return null
}

/**
 * @description 商详底栏单价：默认价目表价；数量满足阶梯时展示优惠单价并划线原价
 * @param {Object|null|undefined} productInfo
 * @param {{ quantity?: number, currentSku?: Object|null }} [options]
 * @returns {{ displayPrice: string, originalPrice: string|null }}
 */
export function resolveDetailBottomBarPriceDisplay(productInfo, options = {}) {
  const quantity = Math.max(1, Math.trunc(Number(options.quantity) || 1))
  const currentSku = options.currentSku ?? null

  let catalogRaw = resolvePromotionDetailCatalogPrice(productInfo, currentSku)
  if (catalogRaw === null || catalogRaw <= 0) {
    catalogRaw = pickPriceValue(currentSku, ['price'])
  }
  if (catalogRaw === null || catalogRaw <= 0) {
    const single = Number(productInfo?.price)
    catalogRaw = Number.isFinite(single) && single > 0 ? single : null
  }

  if (catalogRaw === null || catalogRaw <= 0) {
    return { displayPrice: '0.00', originalPrice: null }
  }

  const promoUnit = resolveDetailQuantityPromoUnitPrice(productInfo, quantity, catalogRaw)
  if (promoUnit !== null && promoUnit < catalogRaw - 0.004) {
    return {
      displayPrice: formatPrice(promoUnit),
      originalPrice: formatPrice(catalogRaw),
    }
  }

  return {
    displayPrice: formatPrice(catalogRaw),
    originalPrice: null,
  }
}
